import type { ChatSessionSummary } from '../../contexts/ChatContext';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import { Box, CircularProgress, IconButton, InputAdornment, Popover, TextField, Typography } from '@mui/material';
import { useMemo, useState } from 'react';

interface ChatSessionHistoryProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  sessions: ChatSessionSummary[];
  loading: boolean;
  activeThreadId: string | null;
  onSelect: (session: ChatSessionSummary) => void;
  onDelete: (threadId: string) => void;
}

function formatRelative(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  return `${Math.floor(months / 12)}y`;
}

export function ChatSessionHistory({
  anchorEl,
  open,
  onClose,
  sessions,
  loading,
  activeThreadId,
  onSelect,
  onDelete,
}: ChatSessionHistoryProps) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter((s) => s.title.toLowerCase().includes(q));
  }, [sessions, query]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Box
        sx={{
          width: 360,
          maxHeight: 440,
          bgcolor: '#1e293b',
          color: 'white',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 2,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search sessions..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.4)' }} />
                  </InputAdornment>
                ),
                sx: {
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.05)',
                  borderRadius: 1.5,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                },
              },
            }}
          />
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={20} sx={{ color: 'white' }} />
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                {sessions.length === 0 ? 'No previous conversations' : 'No matches'}
              </Typography>
            </Box>
          ) : (
            filtered.map((session) => (
              <Box
                key={session.threadId}
                role="button"
                onClick={() => {
                  onSelect(session);
                  onClose();
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  cursor: 'pointer',
                  bgcolor: activeThreadId === session.threadId ? 'rgba(59,130,246,0.15)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    color: 'white',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {session.title}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', flexShrink: 0 }}>
                  {formatRelative(session.updatedAt)}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(session.threadId);
                  }}
                  sx={{
                    color: 'rgba(255,255,255,0.4)',
                    '&:hover': { color: '#f87171', bgcolor: 'rgba(248,113,113,0.08)' },
                  }}
                  aria-label="Delete conversation"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Popover>
  );
}

export default ChatSessionHistory;
